import childSeed from '../data/seedChild';
import networkSeed from '../data/seedNetwork';
import contactSeed from '../data/seedContactEvents';
import mockPeopleResults from '../search/providers/mockPeopleData';
import mockPropertyResults from '../search/providers/mockPropertyData';
import { computeNetworkHealth } from '../utils/networkHealth';
import { daysSince } from '../utils/time';
import { generateId } from '../utils/id';

// In-memory mock NoSQL collections
const children = new Map([[childSeed._id, { ...childSeed }]]);
const networkMembers = new Map();
const relationships = new Map();
const contactEvents = new Map();
const auditLog = [];

// Seed network members and relationships
networkSeed.members.forEach((member) => {
  networkMembers.set(member._id, { ...member });
});
networkSeed.relationships.forEach((relationship) => {
  relationships.set(relationship._id, { ...relationship });
});
contactSeed.forEach((event) => {
  contactEvents.set(event._id, { ...event });
});

const clone = (value) => JSON.parse(JSON.stringify(value));

const buildNetworkResponse = (childId) => {
  const members = Array.from(networkMembers.values()).filter(
    (member) => member.childId === childId
  );
  const edges = Array.from(relationships.values()).filter(
    (relationship) => relationship.childId === childId
  );

  return {
    members: clone(members),
    relationships: clone(edges),
    nodes: members.map((member) => ({
      id: member._id,
      label: member.name,
      role: member.role,
      status: member.status,
      lastContactAt: member.lastContactAt,
      commitmentLevel: member.commitmentLevel
    })),
    edges: edges.map((relationship) => ({
      id: relationship._id,
      source: relationship.memberIdA,
      target: relationship.memberIdB,
      type: relationship.type,
      strength: relationship.strength,
      conflictFlag: relationship.conflictFlag
    }))
  };
};

const appendAudit = (entry) => {
  auditLog.unshift(entry);
  return clone(entry);
};

export const fetchChild = async (childId) => {
  const child = children.get(childId);
  if (!child) {
    throw new Error(`Child ${childId} not found`);
  }
  const network = buildNetworkResponse(childId);
  const health = computeNetworkHealth({
    members: network.members,
    relationships: network.relationships
  });
  return {
    ...clone(child),
    networkHealthScore: health.score,
    networkSummary: health.summary
  };
};

export const fetchNetwork = async (childId) => buildNetworkResponse(childId);

export const fetchContactEvents = async ({ childId, memberId }) => {
  const events = Array.from(contactEvents.values()).filter((event) => {
    if (childId && event.childId !== childId) return false;
    if (memberId && event.memberId !== memberId) return false;
    return true;
  });
  events.sort((a, b) => new Date(b.at) - new Date(a.at));
  return clone(events);
};

export const fetchAuditLog = async ({ entity, entityId }) => {
  return clone(
    auditLog.filter((entry) => {
      if (entity && entry.entity !== entity) return false;
      if (entityId && entry.entityId !== entityId) return false;
      return true;
    })
  );
};

const baseSearchResults = [...mockPeopleResults, ...mockPropertyResults];

export const runSearch = async ({ query, childId }) => {
  if (!query) return [];
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  return baseSearchResults
    .filter((result) => {
      if (result.childId && result.childId !== childId) return false;
      const haystack = `${result.name} ${result.city ?? ''} ${result.state ?? ''}`.toLowerCase();
      return terms.every((term) => haystack.includes(term));
    })
    .map((result) => ({
      ...result,
      alreadyConnected: networkMembers.has(result.matchingMemberId || '')
    }));
};

const nowIso = () => new Date().toISOString();

const resolveStatus = (member) => {
  const days = daysSince(member.lastContactAt);
  if (days === null) return 'unknown';
  if (days <= 30) return 'active';
  if (days <= 60) return 'warming';
  if (days <= 90) return 'cooling';
  return 'inactive';
};

export const createMember = async ({ childId, payload, actor }) => {
  const member = {
    _id: generateId(),
    childId,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    status: 'engaged',
    commitmentLevel: 'unknown',
    childComfort: 'unknown',
    caregiverOpenness: 'unknown',
    lastContactAt: payload.lastContactAt ?? nowIso(),
    sourceProvenance: payload.sourceProvenance ?? ['manual-entry'],
    ...payload
  };
  member.status = resolveStatus(member);
  networkMembers.set(member._id, member);

  const auditEntry = appendAudit({
    _id: generateId(),
    action: 'member.created',
    entity: 'NetworkMember',
    entityId: member._id,
    actor,
    at: nowIso(),
    before: null,
    after: clone(member)
  });

  return { member: clone(member), auditEntry };
};

export const updateMember = async ({ memberId, updates, actor }) => {
  const existing = networkMembers.get(memberId);
  if (!existing) {
    throw new Error(`Member ${memberId} not found`);
  }
  const before = clone(existing);
  const next = {
    ...existing,
    ...updates,
    updatedAt: nowIso()
  };
  next.status = resolveStatus(next);
  networkMembers.set(memberId, next);
  const auditEntry = appendAudit({
    _id: generateId(),
    action: 'member.updated',
    entity: 'NetworkMember',
    entityId: memberId,
    actor,
    at: nowIso(),
    before,
    after: clone(next)
  });
  return { member: clone(next), auditEntry };
};

export const mergeMembers = async ({ sourceId, targetId, actor }) => {
  const source = networkMembers.get(sourceId);
  const target = networkMembers.get(targetId);
  if (!source || !target) {
    throw new Error('Both source and target members must exist to merge');
  }
  const merged = {
    ...target,
    phones: Array.from(new Set([...(target.phones || []), ...(source.phones || [])])),
    emails: Array.from(new Set([...(target.emails || []), ...(source.emails || [])])),
    addresses: Array.from(new Set([...(target.addresses || []), ...(source.addresses || [])])),
    sourceProvenance: Array.from(
      new Set([...(target.sourceProvenance || []), ...(source.sourceProvenance || [])])
    ),
    updatedAt: nowIso()
  };
  merged.status = resolveStatus(merged);
  const before = clone(target);
  networkMembers.delete(sourceId);
  networkMembers.set(targetId, merged);
  const auditEntry = appendAudit({
    _id: generateId(),
    action: 'member.merged',
    entity: 'NetworkMember',
    entityId: targetId,
    actor,
    at: nowIso(),
    before,
    after: clone(merged),
    metadata: { mergedFrom: sourceId }
  });
  return { member: clone(merged), auditEntry };
};

export const createRelationship = async ({ payload, actor }) => {
  const relationship = {
    _id: generateId(),
    createdAt: nowIso(),
    updatedAt: nowIso(),
    strength: payload.strength ?? 3,
    contactFrequency: payload.contactFrequency ?? 'monthly',
    conflictFlag: Boolean(payload.conflictFlag),
    ...payload
  };
  relationships.set(relationship._id, relationship);
  const auditEntry = appendAudit({
    _id: generateId(),
    action: 'relationship.created',
    entity: 'Relationship',
    entityId: relationship._id,
    actor,
    at: nowIso(),
    before: null,
    after: clone(relationship)
  });
  return { relationship: clone(relationship), auditEntry };
};

export const createContactEvent = async ({ payload, actor }) => {
  const event = {
    _id: generateId(),
    createdBy: actor,
    attachments: [],
    ...payload,
    createdAt: nowIso()
  };
  contactEvents.set(event._id, event);
  const auditEntry = appendAudit({
    _id: generateId(),
    action: 'contact.created',
    entity: 'ContactEvent',
    entityId: event._id,
    actor,
    at: nowIso(),
    before: null,
    after: clone(event)
  });

  const member = networkMembers.get(event.memberId);
  let updatedMember = null;
  if (member) {
    member.lastContactAt = event.at;
    member.updatedAt = nowIso();
    member.status = resolveStatus(member);
    networkMembers.set(member._id, member);
    updatedMember = clone(member);
  }

  return { event: clone(event), auditEntry, updatedMember };
};

export const fetchChildAnalytics = async (childId) => {
  const network = buildNetworkResponse(childId);
  const health = computeNetworkHealth({
    members: network.members,
    relationships: network.relationships
  });
  const inactiveMembers = network.members.filter((member) => {
    const days = daysSince(member.lastContactAt);
    return days !== null && days > 60;
  });
  return {
    childId,
    score: health.score,
    summary: health.summary,
    inactiveMembers: inactiveMembers.map((member) => ({
      id: member._id,
      name: member.name,
      lastContactAt: member.lastContactAt,
      daysSinceContact: daysSince(member.lastContactAt)
    })),
    roleCounts: health.roleCounts
  };
};

export const fetchSupervisionAnalytics = async () => {
  const childSummaries = Array.from(children.values()).map((child) => {
    const network = buildNetworkResponse(child._id);
    const health = computeNetworkHealth({
      members: network.members,
      relationships: network.relationships
    });
    const overdue = network.members.filter((member) => {
      const days = daysSince(member.lastContactAt);
      return days !== null && days > 30;
    });
    return {
      childId: child._id,
      childName: `${child.firstName} ${child.lastName}`,
      networkHealthScore: health.score,
      overdueContacts: overdue.length,
      activeMembers: health.summary.activeMembers,
      flags: overdue.map((member) => ({
        memberId: member._id,
        name: member.name,
        daysSinceContact: daysSince(member.lastContactAt)
      }))
    };
  });

  const totalChildren = childSummaries.length;
  const avgScore =
    childSummaries.reduce((acc, summary) => acc + summary.networkHealthScore, 0) /
    Math.max(totalChildren, 1);

  return {
    totalChildren,
    averageNetworkHealth: Math.round(avgScore * 10) / 10,
    lastRefreshedAt: nowIso(),
    children: childSummaries
  };
};

export const fetchSearchSources = async () => {
  return [
    {
      _id: 'mock-people',
      name: 'Mock Kinship Graph',
      type: 'graph',
      termsUrl: 'https://example.org/mock-graph-terms',
      curation: 'hand-curated dataset for demos',
      enabled: true
    },
    {
      _id: 'mock-property',
      name: 'Property & Utility Matches',
      type: 'open-data',
      termsUrl: 'https://example.org/mock-property-terms',
      curation: 'aggregated assessor + utility data',
      enabled: true
    }
  ];
};

export const getAuditLog = () => clone(auditLog);

export const resetMocks = () => {
  // Useful for testing: resets audit log without touching base data
  auditLog.length = 0;
};
